import { useCallback, useMemo, useState } from 'react';
import { useUpdateAutomationWorkflow } from '../../../../api/automations-workflows-client/automations-workflows-client.hooks';
import type { AutomationWorkflow } from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { toast } from '../../../../containers/notifications';
import { computeAutomationCounts, groupAutomations } from './agent-automations.helpers';
import { AutomationFilter, type AutomationCounts, type AutomationGroup } from './agent-automations.type';

type UseAutomationMutationsResult = {
  busyWorkflowId: string | null;
  toggleAutomationActive: (workflow: AutomationWorkflow) => void;
};

/**
 * Hook that exposes per-workflow mutation helpers (currently just the active toggle).
 *
 * @returns {UseAutomationMutationsResult} Helpers plus the id of any workflow currently mutating
 */
function useAutomationMutations(): UseAutomationMutationsResult {
  const updateMutation = useUpdateAutomationWorkflow();
  const [busyWorkflowId, setBusyWorkflowId] = useState<string | null>(null);

  const toggleAutomationActive = useCallback(
    (workflow: AutomationWorkflow): void => {
      setBusyWorkflowId(workflow.id);
      updateMutation.mutate(
        { id: workflow.id, data: { is_active: !workflow.is_active } },
        {
          onSuccess: () => {
            toast.success(workflow.is_active ? 'Automation paused' : 'Automation resumed');
            setBusyWorkflowId(null);
          },
          onError: () => {
            toast.error('Failed to update automation');
            setBusyWorkflowId(null);
          },
        },
      );
    },
    [updateMutation],
  );

  return { busyWorkflowId, toggleAutomationActive };
}

/**
 * Memoized counts for the filter chip row.
 *
 * @param {AutomationWorkflow[]} workflows - All workflows
 * @returns {AutomationCounts} Bucket counts
 */
function useAutomationCounts(workflows: AutomationWorkflow[]): AutomationCounts {
  return useMemo(() => computeAutomationCounts(workflows), [workflows]);
}

/**
 * Memoized list of groups, narrowed by the current filter chip selection.
 *
 * @param {AutomationWorkflow[]} workflows - All workflows
 * @param {AutomationFilter} filter - The active filter chip
 * @returns {AutomationGroup[]} Groups to render
 */
function useAutomationGroups(workflows: AutomationWorkflow[], filter: AutomationFilter): AutomationGroup[] {
  return useMemo(() => {
    const narrowed = workflows.filter((workflow) => {
      if (filter === AutomationFilter.ACTIVE) return workflow.is_active;
      if (filter === AutomationFilter.PAUSED) return !workflow.is_active;
      return true;
    });
    return groupAutomations(narrowed);
  }, [workflows, filter]);
}

export { useAutomationCounts, useAutomationGroups, useAutomationMutations };
